<script setup>
// Otomasyon (reklam kurali) listesi.
//
// Kurallar kendiliginden butce artirip kampanya durdurabildigi icin burada
// yalnizca goruntuleme var: hangi kuralin acik oldugunu ve neyi tetikledigini
// gormek, kurali panelden degistirmekten once gelir.
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { aracCalistir, diziyeCevir, paraBicimle } from './kopru.js';
import AracTablosu from './AracTablosu.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  hesap: { type: String, default: '' },
  hesapAyrinti: { type: Object, default: () => ({}) },
});

const { t } = useI18n();
const route = useRoute();

const satirlar = ref([]);
const yukleniyor = ref(false);
const hata = ref('');
const islemdeki = ref('');
const detay = ref(null);

// Kural butce artirip kampanya durdurabildigi icin tutarlar hesabin kendi
// biriminde bicimlenmeli; TRY varsaymak yanlis rakam gosterir.
const birim = computed(() => props.hesapAyrinti?.currency || 'TRY');

const tarihBicimle = deger => {
  if (!deger) return '—';
  const tarih = new Date(deger);
  return Number.isNaN(tarih.getTime()) ? String(deger) : tarih.toLocaleDateString('tr-TR');
};

// Ayrintida saat de gerekiyor: bir kuralin gun icinde ne zaman guncellendigi
// yalnizca gun gosterilince anlasilmiyor.
const tarihSaatBicimle = deger => {
  if (!deger) return '—';
  const tarih = new Date(deger);
  if (Number.isNaN(tarih.getTime())) return String(deger);
  return tarih.toLocaleString('tr-TR', { dateStyle: 'medium', timeStyle: 'short' });
};

// Kural icin ENABLED/DISABLED "acik/kapali" demektir; reklamdaki gibi
// "duraklatildi" degil. Eslesmeyen kod oldugu gibi kaliyor.
const DURUM_ETIKETLERI = {
  ENABLED: 'META_ADS.ORTAK.DURUM_ACIK',
  DISABLED: 'META_ADS.ORTAK.DURUM_KAPALI',
  ACTIVE: 'META_ADS.ORTAK.DURUM_AKTIF',
  PAUSED: 'META_ADS.ORTAK.DURUM_DURAKLATILDI',
  DELETED: 'META_ADS.ORTAK.DURUM_SILINDI',
  ARCHIVED: 'META_ADS.ORTAK.DURUM_ARSIVLENDI',
};

const durumMetni = deger => {
  const ham = String(deger || '').toUpperCase();
  if (!ham) return '—';
  const ceviri = DURUM_ETIKETLERI[ham];
  return ceviri ? t(ceviri) : ham;
};

// Zamanlama turu de ham kod gelir; karsiligi yoksa kod oldugu gibi kalir.
const SIKLIK_ETIKETLERI = {
  DAILY: 'META_ADS.ALAN.SIKLIK_GUNLUK',
  HOURLY: 'META_ADS.ALAN.SIKLIK_SAATLIK',
  SEMI_HOURLY: 'META_ADS.ALAN.SIKLIK_YARIM_SAAT',
  CONTINUOUS: 'META_ADS.ALAN.SIKLIK_SUREKLI',
  CUSTOM: 'META_ADS.ALAN.SIKLIK_OZEL',
};

const siklikMetni = deger => {
  const ham = String(deger || '').toUpperCase();
  if (!ham) return '—';
  const ceviri = SIKLIK_ETIKETLERI[ham];
  return ceviri ? t(ceviri) : ham;
};

const sutunlar = computed(() => [
  { anahtar: 'name', baslik: t('META_ADS.ORTAK.AD') },
  { anahtar: 'status', baslik: t('META_ADS.ORTAK.DURUM'), bicim: durumMetni },
  {
    anahtar: 'schedule_spec',
    baslik: t('META_ADS.REKLAMLAR.CALISMA_SIKLIGI'),
    // Zamanlama nesnesi ic ice geldigi icin tabloda yalnizca turu gosteriyoruz,
    // tamami detay panelinde.
    bicim: deger => siklikMetni(deger?.schedule_type || deger?.type),
  },
  { anahtar: 'created_time', baslik: t('META_ADS.ORTAK.OLUSTURMA'), bicim: tarihBicimle },
]);

// Bir kuralin ne yaptigi tamamen ic ice nesnelerde yaziyor: kosul
// (evaluation_spec), eylem (execution_spec) ve zamanlama (schedule_spec).
// Bunlari JSON.stringify ile tek satirda basmak kullaniciyi kuralin ne
// yapacagindan habersiz birakiyordu; onun yerine ad -> Turkce etiket, tarih ->
// bicimli deger, ic ice nesne -> "Ust > Alt" satirlari seklinde aciyoruz.
const ALAN_ETIKETLERI = {
  id: 'META_ADS.ALAN.KIMLIK',
  account_id: 'META_ADS.ALAN.HESAP',
  name: 'META_ADS.ORTAK.AD',
  status: 'META_ADS.ORTAK.DURUM',
  created_by: 'META_ADS.ALAN.OLUSTURAN',
  created_time: 'META_ADS.ORTAK.OLUSTURMA',
  updated_time: 'META_ADS.ALAN.GUNCELLEME',
  // Kuralin govdesi.
  evaluation_spec: 'META_ADS.ALAN.KOSUL',
  evaluation_type: 'META_ADS.ALAN.DEGERLENDIRME_TURU',
  filters: 'META_ADS.ALAN.FILTRELER',
  trigger: 'META_ADS.ALAN.TETIKLEYICI',
  execution_spec: 'META_ADS.ALAN.EYLEM',
  execution_type: 'META_ADS.ALAN.EYLEM_TURU',
  execution_options: 'META_ADS.ALAN.EYLEM_SECENEKLERI',
  schedule_spec: 'META_ADS.REKLAMLAR.CALISMA_SIKLIGI',
  schedule_type: 'META_ADS.ALAN.SIKLIK_TURU',
  schedule: 'META_ADS.ALAN.ZAMANLAMA',
  field: 'META_ADS.ALAN.OLCUT',
  operator: 'META_ADS.ALAN.ISLEC',
  value: 'META_ADS.ALAN.DEGER',
};

// Bicimi alan adindan seciyoruz. Kural filtresindeki "value" alanina para
// bicimi UYGULANMIYOR: oradaki sayi kurus degil, kullanicinin yazdigi esik.
const TARIH_ALANLARI = ['created_time', 'updated_time'];
const PARA_ALANLARI = ['daily_budget', 'lifetime_budget', 'bid_amount'];
const DURUM_ALANLARI = ['status'];

// Iki seviyeden derini dar panelde okunmaz hale geliyor; daha derini ve uzun
// dizileri sayiyla ozetliyoruz.
const EN_FAZLA_DERINLIK = 2;
const EN_FAZLA_ACILAN_OGE = 3;

// Bilinmeyen alan adi oldugu gibi kalir; uydurma etiket yanlis bilgi verir.
const etiketle = anahtar => {
  const ceviri = ALAN_ETIKETLERI[anahtar];
  return ceviri ? t(ceviri) : anahtar;
};

const duzDeger = (anahtar, deger) => {
  if (typeof deger === 'boolean') {
    return deger ? t('META_ADS.ORTAK.EVET') : t('META_ADS.ORTAK.HAYIR');
  }
  if (anahtar === 'schedule_type') return siklikMetni(deger);
  if (TARIH_ALANLARI.includes(anahtar)) return tarihSaatBicimle(deger);
  if (PARA_ALANLARI.includes(anahtar)) return paraBicimle(deger, birim.value);
  if (DURUM_ALANLARI.includes(anahtar)) return durumMetni(deger);
  return String(deger);
};

const alanlaraCevir = (nesne, atlanacak = []) => {
  const cikti = [];

  const gez = (kaynak, ustAnahtar, ustEtiket, derinlik) => {
    Object.entries(kaynak || {}).forEach(([anahtar, deger]) => {
      if (atlanacak.includes(anahtar)) return;
      // Bos alan hic gosterilmiyor: "—" satirlari dolu alanlari gozden kacirir.
      if (deger === null || deger === undefined || deger === '') return;

      const tamAnahtar = ustAnahtar ? `${ustAnahtar}.${anahtar}` : anahtar;
      const etiket = ustEtiket ? `${ustEtiket} › ${etiketle(anahtar)}` : etiketle(anahtar);

      if (Array.isArray(deger)) {
        // Bos ogeleri once atiyoruz: aksi halde listede "null" yazisi cikiyor.
        const ogeler = deger.filter(
          oge => oge !== null && oge !== undefined && oge !== ''
        );
        if (!ogeler.length) return;
        if (ogeler.every(oge => typeof oge !== 'object')) {
          cikti.push({
            anahtar: tamAnahtar,
            etiket,
            deger: ogeler.map(oge => duzDeger(anahtar, oge)).join(', '),
          });
          return;
        }
        if (derinlik < EN_FAZLA_DERINLIK && ogeler.length <= EN_FAZLA_ACILAN_OGE) {
          ogeler.forEach((oge, sira) =>
            gez(oge, `${tamAnahtar}.${sira}`, `${etiket} ${sira + 1}`, derinlik + 1)
          );
          return;
        }
        cikti.push({
          anahtar: tamAnahtar,
          etiket,
          deger: t('META_ADS.ORTAK.OGE_SAYISI', { sayi: ogeler.length }),
        });
        return;
      }

      if (typeof deger === 'object') {
        const altSayi = Object.keys(deger).length;
        if (!altSayi) return;
        if (derinlik < EN_FAZLA_DERINLIK) {
          gez(deger, tamAnahtar, etiket, derinlik + 1);
          return;
        }
        cikti.push({
          anahtar: tamAnahtar,
          etiket,
          deger: t('META_ADS.ORTAK.ALAN_SAYISI', { sayi: altSayi }),
        });
        return;
      }

      cikti.push({ anahtar: tamAnahtar, etiket, deger: duzDeger(anahtar, deger) });
    });
  };

  gez(nesne, '', '', 0);
  return cikti;
};

const yukle = async () => {
  if (!props.hesap) return;
  yukleniyor.value = true;
  hata.value = '';
  try {
    const sonuc = await aracCalistir(route.params.accountId, 'ads_get_ad_rules', {
      account_id: props.hesap,
    });
    satirlar.value = diziyeCevir(sonuc);
  } catch (e) {
    hata.value = e.response?.data?.error || e.message;
  } finally {
    yukleniyor.value = false;
  }
};

const detayAc = async satir => {
  islemdeki.value = satir.id;
  hata.value = '';
  try {
    const sonuc = await aracCalistir(route.params.accountId, 'ads_get_rule_details', {
      rule_id: satir.id,
    });
    const veri = Array.isArray(sonuc?.veri) ? sonuc.veri[0] : sonuc?.veri;
    detay.value = {
      ad: satir.name || satir.id,
      alanlar: alanlaraCevir(veri),
      ozet: sonuc?.ozet || '',
    };
  } catch (e) {
    hata.value = e.response?.data?.error || e.message;
  } finally {
    islemdeki.value = '';
  }
};

watch(() => props.hesap, yukle);
onMounted(yukle);
</script>

<template>
  <div class="p-6">
    <div class="flex items-center gap-3 mb-4">
      <h2 class="text-base font-medium text-n-slate-12">
        {{ t('META_ADS.REKLAMLAR.OTOMASYONLAR') }}
      </h2>
      <span class="grow" />
      <Button
        variant="faded"
        size="sm"
        icon="i-lucide-refresh-cw"
        :label="t('META_ADS.ORTAK.YENILE')"
        @click="yukle"
      />
    </div>

    <p v-if="hata" class="mb-3 text-sm text-n-ruby-11">{{ hata }}</p>
    <p v-if="!hesap" class="text-sm text-n-slate-11">{{ t('META_ADS.ORTAK.HESAP_SECIN') }}</p>

    <div v-else class="flex items-start gap-6">
      <div class="flex-1 min-w-0">
        <AracTablosu
          :satirlar="satirlar"
          :sutunlar="sutunlar"
          :yukleniyor="yukleniyor"
          :bos-metin="t('META_ADS.REKLAMLAR.KURAL_YOK')"
        >
          <template #eylemler="{ satir }">
            <Button
              variant="faded"
              size="sm"
              :label="t('META_ADS.ORTAK.DETAY')"
              :disabled="islemdeki === satir.id"
              @click="detayAc(satir)"
            />
          </template>
        </AracTablosu>
      </div>

      <aside
        v-if="detay"
        class="w-80 shrink-0 p-4 border rounded-lg border-n-weak bg-n-surface-1"
      >
        <div class="flex items-start gap-2 mb-3">
          <h3 class="text-sm font-medium text-n-slate-12 break-words">{{ detay.ad }}</h3>
          <span class="grow" />
          <Button variant="faded" size="sm" icon="i-lucide-x" @click="detay = null" />
        </div>
        <p v-if="detay.ozet" class="mb-3 text-xs text-n-slate-11">{{ detay.ozet }}</p>
        <dl
          v-if="detay.alanlar.length"
          class="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 text-xs"
        >
          <template v-for="alan in detay.alanlar" :key="alan.anahtar">
            <dt class="break-words text-n-slate-11">{{ alan.etiket }}</dt>
            <dd class="break-words text-n-slate-12">{{ alan.deger }}</dd>
          </template>
        </dl>
        <p v-else class="text-xs text-n-slate-11">
          {{ t('META_ADS.ORTAK.DETAY_YOK') }}
        </p>
      </aside>
    </div>
  </div>
</template>
